import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import USPSApi from '../utils/uspsApi';
import '../styles/databricks-usps.css';

const DatabricksUSPS = ({ user }) => {
  const [warehouseInfo, setWarehouseInfo] = useState(null);
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: '2024-08-25',
    end: '2024-08-31'
  });
  const [groupBy, setGroupBy] = useState('none');
  const [sortBy, setSortBy] = useState('packages');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading health data...');
      const health = await USPSApi.getHealth();
      setWarehouseInfo(health);
      
      console.log('Loading KPI data...');
      const kpis = await USPSApi.getKPIs(dateRange.start, dateRange.end);
      console.log('KPI data received, processing...');
      
      setKpiData(Array.isArray(kpis) ? kpis : []);
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const refreshKPIs = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Refreshing KPI data for:', dateRange);
      const kpis = await USPSApi.getKPIs(dateRange.start, dateRange.end);
      console.log('KPI refresh complete');
      setKpiData(Array.isArray(kpis) ? kpis : []);
    } catch (err) {
      console.error('Refresh error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="databricks-loading">
          <h2>Loading USPS Data Pipeline Dashboard...</h2>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="databricks-usps-page">
        <div className="usps-header">
          <div className="usps-logo-section">
            <div className="usps-logo">
              <div className="eagle-emblem">🦅</div>
              <div className="usps-text">
                <span className="usps-title">UNITED STATES</span>
                <span className="postal-service">POSTAL SERVICE</span>
              </div>
            </div>
          </div>
          <div className="hero-section">
            <h1>USPS Operations Dashboard</h1>
            <p className="hero-subtitle">Real-time Package Delivery Analytics & Performance Metrics</p>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <p>⚠️ {error}</p>
          </div>
        )}

        <div className="content-container">
          {/* Warehouse Status */}
          <section className="warehouse-status">
            <h2>📊 System Status</h2>
            {warehouseInfo && (
              <div className="status-grid">
                <div className="status-item">
                  <h3>Warehouse ID</h3>
                  <p>{warehouseInfo.warehouse_id}</p>
                </div>
                <div className="status-item">
                  <h3>Catalog</h3>
                  <p>{warehouseInfo.catalog}</p>
                </div>
                <div className="status-item">
                  <h3>Schema</h3>
                  <p>{warehouseInfo.schema}</p>
                </div>
                <div className="status-item">
                  <h3>Connection</h3>
                  <p className="status-active">✅ {warehouseInfo ? 'Connected' : 'Disconnected'}</p>
                </div>
              </div>
            )}
          </section>

          {/* Data Pipeline Architecture */}
          <section className="pipeline-architecture">
            <h2>🚛 Data Processing Pipeline</h2>
            <div className="pipeline-flow">
              <div className="pipeline-stage bronze">
                <h3>🥉 Bronze Layer</h3>
                <p>Raw USPS operational data ingestion</p>
                <ul>
                  <li>Package tracking events</li>
                  <li>Delivery confirmations</li>
                  <li>Route optimization data</li>
                </ul>
              </div>
              <div className="pipeline-arrow">→</div>
              <div className="pipeline-stage silver">
                <h3>🥈 Silver Layer</h3>
                <p>Cleaned and validated data</p>
                <ul>
                  <li>Data quality checks</li>
                  <li>Schema validation</li>
                  <li>Duplicate removal</li>
                </ul>
              </div>
              <div className="pipeline-arrow">→</div>
              <div className="pipeline-stage gold">
                <h3>🥇 Gold Layer</h3>
                <p>Business-ready analytics tables</p>
                <ul>
                  <li>KPI aggregations</li>
                  <li>Performance metrics</li>
                  <li>Operational insights</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Operations Dashboard */}
          <section className="operations-dashboard">
            <h2>📦 Package Delivery Operations</h2>
            {kpiData.length > 0 && (() => {
              // Calculate totals from API-filtered data
              const totalPackages = kpiData.reduce((sum, item) => sum + (item.delivered_packages || 0), 0);
              const uniqueZip3s = new Set([...kpiData.map(item => item.origin_zip3), ...kpiData.map(item => item.destination_zip3)]).size;
              const avgTransitHours = kpiData.length > 0 
                ? kpiData.reduce((sum, item) => sum + (item.avg_transit_hours || 0), 0) / kpiData.length 
                : 0;
              
              // For demo: these represent packages delivered in the selected period
              
              return (
                <div className="ops-summary">
                  <div className="ops-card">
                    <h4>Delivered Packages ({dateRange.start} to {dateRange.end})</h4>
                    <div className="ops-metrics">
                      <div className="metric">
                        <span className="metric-label">Total Delivered</span>
                        <span className="metric-value">{totalPackages?.toLocaleString()}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Unique ZIP3s</span>
                        <span className="metric-value">{uniqueZip3s?.toLocaleString()}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Avg per ZIP3</span>
                        <span className="metric-value">{uniqueZip3s > 0 ? (totalPackages / uniqueZip3s).toFixed(1) : '0'}</span>
                      </div>
                    </div>
                    <p className="last-scan">Avg Transit Time: {avgTransitHours.toFixed(1)} hours</p>
                  </div>
                </div>
              );
            })()}
          </section>

          {/* KPI Analytics */}
          <section className="kpi-analytics">
            <h2>📈 Delivery Performance Metrics</h2>
            <div className="date-controls">
              <label>
                Start Date:
                <input 
                  type="date" 
                  value={dateRange.start}
                  min="2024-08-25"
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
              </label>
              <label>
                End Date:
                <input 
                  type="date" 
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
              </label>
              <div className="group-controls">
                <label>
                  <input 
                    type="radio" 
                    name="groupBy" 
                    value="none" 
                    checked={groupBy === 'none'}
                    onChange={(e) => setGroupBy(e.target.value)}
                  />
                  Show Individual Routes
                </label>
                <label>
                  <input 
                    type="radio" 
                    name="groupBy" 
                    value="origin" 
                    checked={groupBy === 'origin'}
                    onChange={(e) => setGroupBy(e.target.value)}
                  />
                  Group by Origin ZIP3
                </label>
                <label>
                  <input 
                    type="radio" 
                    name="groupBy" 
                    value="destination" 
                    checked={groupBy === 'destination'}
                    onChange={(e) => setGroupBy(e.target.value)}
                  />
                  Group by Destination ZIP3
                </label>
              </div>
              <button onClick={refreshKPIs} className="refresh-btn" disabled={loading}>
                {loading ? 'Loading...' : 'Refresh Data'}
              </button>
            </div>
            
            {kpiData.length > 0 ? (() => {
              let displayData;
              
              if (groupBy === 'none') {
                // Show individual routes
                displayData = kpiData.map(item => ({
                  origin_zip3: item.origin_zip3,
                  destination_zip3: item.destination_zip3,
                  event_date: item.event_date,
                  packages: item.delivered_packages,
                  avgTransitHours: item.avg_transit_hours,
                  p50TransitHours: item.p50_transit_hours,
                  p90TransitHours: item.p90_transit_hours
                })).sort((a, b) => {
                  const aVal = a[sortBy] || a.packages;
                  const bVal = b[sortBy] || b.packages;
                  if (sortOrder === 'asc') {
                    return aVal > bVal ? 1 : -1;
                  } else {
                    return aVal < bVal ? 1 : -1;
                  }
                });
              } else {
                // Group data by selected criteria
                const groupedData = kpiData.reduce((acc, item) => {
                  const key = groupBy === 'origin' ? item.origin_zip3 : item.destination_zip3;
                  if (!acc[key]) {
                    acc[key] = {
                      zip3: key,
                      packages: 0,
                      transitHours: [],
                      dates: new Set()
                    };
                  }
                  acc[key].packages += item.delivered_packages || 0;
                  acc[key].transitHours.push(item.avg_transit_hours || 0);
                  acc[key].dates.add(item.event_date);
                  return acc;
                }, {});
                
                // Calculate aggregated metrics
                displayData = Object.values(groupedData).map(group => {
                  const sortedHours = group.transitHours.sort((a, b) => a - b);
                  const avg = sortedHours.reduce((sum, h) => sum + h, 0) / sortedHours.length;
                  const p50 = sortedHours[Math.floor(sortedHours.length * 0.5)];
                  const p90 = sortedHours[Math.floor(sortedHours.length * 0.9)];
                  
                  return {
                    zip3: group.zip3,
                    packages: group.packages,
                    dateCount: group.dates.size,
                    avgTransitHours: avg,
                    p50TransitHours: p50,
                    p90TransitHours: p90
                  };
                }).sort((a, b) => {
                  const aVal = a[sortBy];
                  const bVal = b[sortBy];
                  if (sortOrder === 'asc') {
                    return aVal > bVal ? 1 : -1;
                  } else {
                    return aVal < bVal ? 1 : -1;
                  }
                });
              }
              
              const handleSort = (column) => {
                if (sortBy === column) {
                  setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                } else {
                  setSortBy(column);
                  setSortOrder('desc');
                }
              };
              
              return (
                <div className="kpi-table-container">
                  <table className="kpi-table">
                    <thead>
                      <tr>
                        {groupBy === 'none' ? (
                          <>
                            <th onClick={() => handleSort('event_date')} className="sortable">
                              Date
                              {sortBy === 'event_date' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('origin_zip3')} className="sortable">
                              Origin ZIP3
                              {sortBy === 'origin_zip3' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('destination_zip3')} className="sortable">
                              Destination ZIP3
                              {sortBy === 'destination_zip3' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('packages')} className="sortable">
                              Packages
                              {sortBy === 'packages' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('avgTransitHours')} className="sortable">
                              Avg Transit Hours
                              {sortBy === 'avgTransitHours' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('p50TransitHours')} className="sortable">
                              P50 Transit Hours
                              {sortBy === 'p50TransitHours' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('p90TransitHours')} className="sortable">
                              P90 Transit Hours
                              {sortBy === 'p90TransitHours' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                          </>
                        ) : (
                          <>
                            <th onClick={() => handleSort('zip3')} className="sortable">
                              {groupBy === 'origin' ? 'Origin' : 'Destination'} ZIP3
                              {sortBy === 'zip3' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('packages')} className="sortable">
                              Total Packages
                              {sortBy === 'packages' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('dateCount')} className="sortable">
                              Days Active
                              {sortBy === 'dateCount' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('avgTransitHours')} className="sortable">
                              Avg Transit Hours
                              {sortBy === 'avgTransitHours' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('p50TransitHours')} className="sortable">
                              P50 Transit Hours
                              {sortBy === 'p50TransitHours' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                            <th onClick={() => handleSort('p90TransitHours')} className="sortable">
                              P90 Transit Hours
                              {sortBy === 'p90TransitHours' && <span className="sort-arrow">{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {displayData.map((item, index) => (
                        <tr key={index}>
                          {groupBy === 'none' ? (
                            <>
                              <td>{item.event_date}</td>
                              <td>{item.origin_zip3}</td>
                              <td>{item.destination_zip3}</td>
                              <td>{item.packages?.toLocaleString()}</td>
                              <td>{item.avgTransitHours ? `${item.avgTransitHours.toFixed(1)}h` : 'N/A'}</td>
                              <td>{item.p50TransitHours ? `${item.p50TransitHours.toFixed(1)}h` : 'N/A'}</td>
                              <td>{item.p90TransitHours ? `${item.p90TransitHours.toFixed(1)}h` : 'N/A'}</td>
                            </>
                          ) : (
                            <>
                              <td>{item.zip3}</td>
                              <td>{item.packages?.toLocaleString()}</td>
                              <td>{item.dateCount}</td>
                              <td>{item.avgTransitHours ? `${item.avgTransitHours.toFixed(1)}h` : 'N/A'}</td>
                              <td>{item.p50TransitHours ? `${item.p50TransitHours.toFixed(1)}h` : 'N/A'}</td>
                              <td>{item.p90TransitHours ? `${item.p90TransitHours.toFixed(1)}h` : 'N/A'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="data-info">
                    {groupBy === 'none' 
                      ? `Showing all ${displayData.length} individual routes` 
                      : `Showing all ${displayData.length} ${groupBy === 'origin' ? 'origin' : 'destination'} ZIP3s`
                    }
                  </p>
                </div>
              );
            })() : (
              <p>No KPI data available for selected date range</p>
            )}
          </section>

          {/* Databricks Benefits */}
          <section className="databricks-benefits">
            <h2>⚙️ Technology Infrastructure</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>🔄 Real-time Processing</h3>
                <p>Process millions of package events in real-time with Delta Lake</p>
              </div>
              <div className="benefit-item">
                <h3>📊 Unified Analytics</h3>
                <p>Combine batch and streaming data for comprehensive insights</p>
              </div>
              <div className="benefit-item">
                <h3>🤖 ML Integration</h3>
                <p>Built-in MLflow for predictive delivery optimization</p>
              </div>
              <div className="benefit-item">
                <h3>💰 Cost Optimization</h3>
                <p>Auto-scaling compute reduces infrastructure costs by 40%</p>
              </div>
            </div>
          </section>

          {/* Technical Notes */}
          <section className="technical-notes">
            <h2>🔧 System Architecture Details</h2>
            <div className="notes-container">
              <div className="note-item">
                <h4>Data Ingestion</h4>
                <p>• Auto Loader for continuous file ingestion from S3</p>
                <p>• Change Data Capture (CDC) for real-time updates</p>
                <p>• Schema evolution handling for data format changes</p>
              </div>
              <div className="note-item">
                <h4>Data Quality</h4>
                <p>• Delta Live Tables for automated data validation</p>
                <p>• Expectation-based quality checks</p>
                <p>• Automated quarantine of bad data</p>
              </div>
              <div className="note-item">
                <h4>Performance Optimization</h4>
                <p>• Z-ordering for optimal query performance</p>
                <p>• Liquid clustering for dynamic optimization</p>
                <p>• Photon engine for 3x faster analytics</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default DatabricksUSPS;